// 100ms Video Room Component for MyCounsellorOnline (React 19 compatible)
import React, { useEffect, useRef, useState, useContext } from 'react';
import useHMS from '../hooks/useHMS';
import { Mic, MicOff, Video, VideoOff, PhoneOff, MessageCircle, Send, Users, X, Clock, AlertTriangle, Plus, CalendarPlus } from 'lucide-react';
import { AuthContext } from '../App';
import SessionExtension from './SessionExtension';
import InSessionRequests from './InSessionRequests';
import InSessionBooking from './InSessionBooking';

// Connection Quality Indicator Component
function ConnectionQualityIndicator({ quality }) {
  // Quality score: 0 = unknown, 1 = very poor, 2 = poor, 3 = moderate, 4 = good, 5 = excellent
  const getQualityInfo = (score) => {
    if (!score || score === 0) return { label: 'Connecting...', color: 'bg-gray-400', bars: 0 };
    if (score <= 1) return { label: 'Poor', color: 'bg-red-500', bars: 1 };
    if (score <= 2) return { label: 'Weak', color: 'bg-orange-500', bars: 2 };
    if (score <= 3) return { label: 'Moderate', color: 'bg-yellow-500', bars: 3 };
    if (score <= 4) return { label: 'Good', color: 'bg-green-400', bars: 4 };
    return { label: 'Excellent', color: 'bg-green-500', bars: 5 };
  };

  const qualityScore = quality?.downlinkQuality || 0;
  const { label, color, bars } = getQualityInfo(qualityScore);

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm" data-testid="connection-quality">
      <div className="flex items-end gap-0.5 h-4">
        {[1, 2, 3, 4, 5].map((bar) => (
          <div
            key={bar}
            className={`w-1 rounded-sm transition-all ${
              bar <= bars ? color : 'bg-gray-600'
            }`}
            style={{ height: `${bar * 3 + 2}px` }}
          />
        ))}
      </div>
      <span className="text-xs text-white font-medium">{label}</span>
    </div>
  );
}

// Session Countdown Timer Component
function SessionTimer({ durationMinutes, bonusSeconds = 0, onTimeUp, onWarning }) {
  const [timeRemaining, setTimeRemaining] = useState(durationMinutes * 60);
  const [isPaused, setIsPaused] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const warningShownRef = useRef(false);
  const prevBonusRef = useRef(0);

  // When a paid extension is applied, add the delta to the running countdown
  useEffect(() => {
    const delta = bonusSeconds - prevBonusRef.current;
    if (delta > 0) {
      prevBonusRef.current = bonusSeconds;
      warningShownRef.current = false;
      setTimeRemaining((t) => t + delta);
    }
  }, [bonusSeconds]);
  
  useEffect(() => {
    if (isPaused || timeRemaining <= 0) return;
    
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        
        // Show warning at 7 minutes (420 seconds)
        if (newTime <= 420 && newTime > 417 && !warningShownRef.current) {
          warningShownRef.current = true;
          setShowWarning(true);
          onWarning && onWarning();
          // Auto-hide warning after 10 seconds
          setTimeout(() => setShowWarning(false), 10000);
        }
        
        // Time's up
        if (newTime <= 0) {
          onTimeUp && onTimeUp();
          return 0;
        }
        
        return newTime;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isPaused, timeRemaining, onTimeUp, onWarning]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const isLowTime = timeRemaining <= 420; // 7 minutes
  const isCritical = timeRemaining <= 120; // 2 minutes
  
  return (
    <>
      {/* Timer Display */}
      <div 
        className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm ${
          isCritical ? 'bg-red-500/80 animate-pulse' : 
          isLowTime ? 'bg-orange-500/80' : 
          'bg-black/40'
        }`}
        data-testid="session-timer"
      >
        <Clock size={18} className="text-white" />
        <span className={`font-mono text-lg font-bold ${isCritical ? 'text-white' : 'text-white'}`}>
          {formatTime(timeRemaining)}
        </span>
        {isPaused && <span className="text-xs text-white/70">(Paused)</span>}
      </div>
      
      {/* 7-minute Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="time-warning">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 text-center shadow-2xl animate-bounce-in">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">7 Minutes Remaining</h3>
            <p className="text-gray-600 mb-4">
              Your session will end soon. Please wrap up your conversation or extend the session.
            </p>
            <button
              onClick={() => setShowWarning(false)}
              className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Individual Peer Video Component
function PeerVideo({ peer, isLocal, attachVideo, detachVideo }) {
  const videoRef = useRef(null);
  const trackIdRef = useRef(null);
  
  useEffect(() => {
    const videoEl = videoRef.current;
    const trackId = peer?.videoTrack;
    
    // Only attach/detach if trackId changed
    if (videoEl && trackId && trackId !== trackIdRef.current) {
      trackIdRef.current = trackId;
      attachVideo(trackId, videoEl);
    }
    
    return () => {
      if (videoEl && trackIdRef.current) {
        detachVideo(trackIdRef.current, videoEl);
        trackIdRef.current = null;
      }
    };
  }, [peer?.videoTrack, attachVideo, detachVideo]);
  
  return (
    <div className={`relative rounded-xl overflow-hidden bg-gray-900 ${isLocal ? 'w-32 h-24 absolute bottom-4 right-4 z-10' : 'w-full h-full'}`}>
      <video
        ref={videoRef}
        autoPlay
        muted={isLocal}
        playsInline
        className="w-full h-full object-cover"
      />
      {!peer?.videoTrack && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-2xl font-bold text-primary">
              {peer?.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
        </div>
      )}
      <div className="absolute bottom-2 left-2 bg-black/50 px-2 py-1 rounded text-white text-xs">
        {peer?.name} {isLocal && '(You)'}
      </div>
      {!peer?.audioTrack && (
        <div className="absolute top-2 right-2 bg-red-500 rounded-full p-1">
          <MicOff size={12} className="text-white" />
        </div>
      )}
    </div>
  );
}

// Chat Panel Component
function ChatPanel({ messages, onSendMessage, onClose }) {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (newMessage.trim()) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
    }
  };

  return (
    <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl flex flex-col z-20">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-[#2D3748]">Chat</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X size={20} className="text-[#718096]" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <p className="text-center text-[#A0AEC0] text-sm">No messages yet</p>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`${msg.senderName === 'You' ? 'text-right' : ''}`}>
            <div className={`inline-block px-3 py-2 rounded-xl max-w-[80%] ${
              msg.senderName === 'You' 
                ? 'bg-primary text-white' 
                : 'bg-gray-100 text-[#2D3748]'
            }`}>
              <p className="text-xs font-medium mb-1 opacity-70">{msg.senderName}</p>
              <p className="text-sm">{msg.message}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={handleSend} className="p-4 border-t flex gap-2">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-primary"
        />
        <button 
          type="submit" 
          disabled={!newMessage.trim()}
          className="p-2 bg-primary text-white rounded-lg disabled:opacity-50"
        >
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}

// Main Video Room Component
export default function HMSVideoRoom({ sessionId, onLeave, channel = 'video', durationMinutes = 30, onTimeUp }) {
  const { user } = useContext(AuthContext);
  
  // Use the shared HMS hook - state is already managed by the singleton
  const {
    isConnected,
    isAudioEnabled,
    isVideoEnabled,
    peers,
    messages,
    connectionQuality,
    toggleAudio,
    toggleVideo,
    sendMessage,
    leaveRoom,
    attachVideo,
    detachVideo
  } = useHMS(sessionId);
  
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [bonusSeconds, setBonusSeconds] = useState(0);
  const [showExtend, setShowExtend] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const isSeeker = user?.role === 'care_seeker';
  const isProvider = user?.role === 'care_provider';

  const localPeer = peers.find(p => p.isLocal);
  const remotePeers = peers.filter(p => !p.isLocal);

  const handleLeave = async () => {
    setShowEndConfirm(false);
    await leaveRoom();
    onLeave && onLeave();
  };
  
  const handleTimeWarning = () => {
    // Play a sound or show additional notification
    console.log('7 minutes remaining warning triggered');
  };
  
  const handleTimeUp = () => {
    // Auto-end session when time is up
    handleLeave();
  };

  // Show loading only if not connected - but don't loop
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center" data-testid="hms-connecting">
        <div className="text-white text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Connecting to room...</p>
          <p className="text-sm text-gray-400 mt-2">Please wait while we establish connection</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 flex flex-col" data-testid="hms-video-room">
      {/* Main Video Area */}
      <div className="flex-1 relative">
        {/* Top Bar: Timer, Close Button, and Connection Quality */}
        <div className="absolute top-4 left-4 right-4 z-30 flex items-center justify-between">
          <ConnectionQualityIndicator quality={connectionQuality} />
          
          {/* Prominent Close Session Button */}
          <button
            onClick={() => setShowEndConfirm(true)}
            data-testid="close-session-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-full font-semibold shadow-lg transition-all hover:scale-105 active:scale-95"
          >
            <X size={18} />
            Close Session
          </button>
          
          <SessionTimer 
            durationMinutes={durationMinutes} 
            bonusSeconds={bonusSeconds}
            onTimeUp={handleTimeUp}
            onWarning={handleTimeWarning}
          />
        </div>

        {/* Provider: incoming in-session requests (extension / booking) */}
        {isProvider && (
          <div className="absolute bottom-4 left-4 z-30 w-80 max-w-[80vw]">
            <InSessionRequests sessionId={sessionId} user={user} />
          </div>
        )}
        
        {/* Remote Peer (Main View) */}
        {remotePeers.length > 0 ? (
          <PeerVideo 
            peer={remotePeers[0]} 
            isLocal={false} 
            attachVideo={attachVideo}
            detachVideo={detachVideo}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-800">
            <div className="text-center text-white">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg">Waiting for others to join...</p>
              <p className="text-sm opacity-70 mt-2">Share the session link with the other participant</p>
            </div>
          </div>
        )}
        
        {/* Local Peer (Picture-in-Picture) */}
        {localPeer && channel === 'video' && (
          <PeerVideo 
            peer={localPeer} 
            isLocal={true}
            attachVideo={attachVideo}
            detachVideo={detachVideo}
          />
        )}
        
        {/* Participants Panel */}
        {showParticipants && (
          <div className="absolute left-4 top-16 bg-white rounded-xl shadow-xl p-4 z-20 w-64">
            <h3 className="font-semibold text-[#2D3748] mb-3">Participants ({peers.length})</h3>
            <div className="space-y-2">
              {peers.map((peer) => (
                <div key={peer.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">{peer.name?.charAt(0)}</span>
                  </div>
                  <span className="text-sm text-[#2D3748] flex-1">{peer.name}</span>
                  {peer.isLocal && <span className="text-xs text-[#A0AEC0]">(You)</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Chat Panel */}
        {showChat && (
          <ChatPanel 
            messages={messages.map(m => ({
              senderName: m.senderName || 'Unknown',
              message: m.message
            }))}
            onSendMessage={sendMessage}
            onClose={() => setShowChat(false)}
          />
        )}
      </div>

      {/* Controls Bar */}
      <div className="bg-gray-800 px-4 py-4">
        <div className="flex items-center justify-center gap-4">
          {/* Audio Toggle */}
          <button
            onClick={toggleAudio}
            data-testid="toggle-audio"
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              isAudioEnabled ? 'bg-gray-600 hover:bg-gray-500' : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {isAudioEnabled ? (
              <Mic size={20} className="text-white" />
            ) : (
              <MicOff size={20} className="text-white" />
            )}
          </button>
          
          {/* Video Toggle (only for video sessions) */}
          {channel === 'video' && (
            <button
              onClick={toggleVideo}
              data-testid="toggle-video"
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                isVideoEnabled ? 'bg-gray-600 hover:bg-gray-500' : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              {isVideoEnabled ? (
                <Video size={20} className="text-white" />
              ) : (
                <VideoOff size={20} className="text-white" />
              )}
            </button>
          )}
          
          {/* Chat Toggle */}
          <button
            onClick={() => setShowChat(!showChat)}
            data-testid="toggle-chat"
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              showChat ? 'bg-primary' : 'bg-gray-600 hover:bg-gray-500'
            }`}
          >
            <MessageCircle size={20} className="text-white" />
          </button>
          
          {/* Participants Toggle */}
          <button
            onClick={() => setShowParticipants(!showParticipants)}
            data-testid="toggle-participants"
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              showParticipants ? 'bg-primary' : 'bg-gray-600 hover:bg-gray-500'
            }`}
          >
            <Users size={20} className="text-white" />
          </button>

          {/* Seeker: Extend + Book Therapy */}
          {isSeeker && (
            <>
              <button
                onClick={() => setShowExtend(true)}
                data-testid="call-extend-btn"
                className="h-12 px-4 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center gap-2 text-white text-sm font-medium transition-colors"
              >
                <Plus size={18} /> Extend
              </button>
              <button
                onClick={() => setShowBook(true)}
                data-testid="call-book-therapy-btn"
                className="h-12 px-4 rounded-full bg-primary hover:opacity-90 flex items-center gap-2 text-white text-sm font-medium transition-colors"
              >
                <CalendarPlus size={18} /> Book Therapy
              </button>
            </>
          )}
          
          {/* Leave Button - Shows confirmation */}
          <button
            onClick={() => setShowEndConfirm(true)}
            data-testid="leave-room"
            className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
          >
            <PhoneOff size={20} className="text-white" />
          </button>
        </div>
      </div>
      
      {/* Seeker: Extension modal */}
      {showExtend && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="call-extend-modal">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-800">Extend Session</h3>
              <button onClick={() => setShowExtend(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <SessionExtension
                sessionId={sessionId}
                session={{ channel }}
                user={user}
                onExtensionPaid={({ minutes }) => {
                  setBonusSeconds((s) => s + minutes * 60);
                  setTimeout(() => setShowExtend(false), 1200);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Seeker: Therapy booking modal */}
      {showBook && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="call-book-modal">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-800">Book a Therapy Session</h3>
              <button onClick={() => setShowBook(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <InSessionBooking
                sessionId={sessionId}
                user={user}
                defaultOpen={true}
                onBooked={() => setTimeout(() => setShowBook(false), 1500)}
              />
            </div>
          </div>
        </div>
      )}

      {/* End Session Confirmation Modal */}
      {showEndConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="end-confirm-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PhoneOff size={32} className="text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">End Session?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to end this session? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowEndConfirm(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleLeave}
                data-testid="confirm-end-session"
                className="px-6 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
